import { resolveCanonicalLotKey, type LotIdentityCandidate } from './lotIdentity'
import { getZonedIsoDate, resolveTimeZone } from './timeBucketing'

// v2.2 T3-B-06f: `toDateMode` lets an operator opt into a rolling
// "always up to today" temporal window without editing the persisted
// `toDate` every morning. Default `'fixed'` matches pre-06f behavior so
// existing saved scopes migrate cleanly; `'today'` is resolved at fetch
// time by the dataset consumer (see `resolveScopeToDate` below).
//
// `fromDateMode` is defined as a single-value enum for now — leaves
// room for `'start-of-week'` / `'start-of-month'` / `'stocking-date'`
// in v2.3 without a second schema migration. Only `'fixed'` is
// meaningful today.
export type ReportScopeDateMode = 'fixed' | 'today'
export type ReportScopeFromDateMode = 'fixed'

export type ReportScopeV1 = {
  fromDate: string
  toDate: string
  fromDateMode?: ReportScopeFromDateMode
  toDateMode?: ReportScopeDateMode
  farmIds: string[]
  productionUnitIds: string[]
  lotIds: string[]
  lotKeys: string[]
  /**
   * v2.2 findings.md #188: 'reconciled' spreads each exact count's
   * difference back to where it started in the stock-based columns.
   * Absent / 'recorded' = the stock as recorded (every saved report so far).
   */
  stockView?: ReportStockView
}

export type ReportStockView = 'recorded' | 'reconciled'

export type ReportRow = Record<string, unknown>

const normalizeStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
}

const normalizeToDateMode = (value: unknown): ReportScopeDateMode => {
  return value === 'today' ? 'today' : 'fixed'
}

const normalizeFromDateMode = (_value: unknown): ReportScopeFromDateMode => {
  // Only 'fixed' is meaningful today; unknown values coerce to 'fixed'
  // so future v2.3 additions can extend the enum without breaking older
  // clients that persisted a not-yet-recognized value.
  return 'fixed'
}

export const normalizeReportScope = (value: unknown): ReportScopeV1 | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>

  const fromDate = typeof record.fromDate === 'string' ? record.fromDate.trim() : ''
  const toDate = typeof record.toDate === 'string' ? record.toDate.trim() : ''
  const toDateMode = normalizeToDateMode(record.toDateMode)
  const fromDateMode = normalizeFromDateMode(record.fromDateMode)
  const farmIds = normalizeStringList(record.farmIds)
  const productionUnitIds = normalizeStringList(record.productionUnitIds)
  const lotIds = normalizeStringList(record.lotIds)
  const lotKeys = normalizeStringList(record.lotKeys)
  const stockView: ReportStockView = record.stockView === 'reconciled' ? 'reconciled' : 'recorded'

  // v2.2 T3-B-06f: a non-fixed `toDateMode` (currently only `'today'`)
  // is itself a scope constraint even when every other field is empty,
  // so treat it as a real scope rather than returning null. Preserves
  // the pre-06f "empty scope = null" semantics for the fixed default.
  const isEmpty =
    !fromDate
    && !toDate
    && toDateMode === 'fixed'
    && farmIds.length === 0
    && productionUnitIds.length === 0
    && lotIds.length === 0
    && lotKeys.length === 0
    && stockView === 'recorded'

  if (isEmpty) {
    return null
  }

  return {
    fromDate,
    toDate,
    fromDateMode,
    toDateMode,
    farmIds,
    productionUnitIds,
    lotIds,
    lotKeys,
    ...(stockView === 'reconciled' ? { stockView } : {})
  }
}

/**
 * v2.2 T3-B-06f: resolve a scope's `toDate` for consumers that need a
 * concrete calendar date. When `toDateMode === 'today'`, returns
 * today's YYYY-MM-DD in the caller-provided timezone (falls back to
 * the browser timezone via `resolveTimeZone`). When `toDateMode ===
 * 'fixed'` (or undefined), returns the persisted `toDate` unchanged.
 *
 * Pure — depends only on `scope`, `now`, and `timeZone`. Callers
 * inject `now` in tests to freeze the clock.
 */
export const resolveScopeToDate = (
  scope: ReportScopeV1 | null | undefined,
  opts?: { now?: Date; timeZone?: string }
): string => {
  if (!scope) return ''
  if (scope.toDateMode !== 'today') return scope.toDate
  const now = opts?.now ?? new Date()
  const tz = resolveTimeZone(opts?.timeZone)
  return getZonedIsoDate(now, tz) ?? scope.toDate
}

const toTrimmedString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const toLotIdentityCandidate = (value: unknown): LotIdentityCandidate => {
  if (typeof value === 'string' || typeof value === 'number') return value
  if (value === null || value === undefined) return value
  return undefined
}

const resolveReportLotToken = (...values: unknown[]): string | null => {
  const token = resolveCanonicalLotKey(...values.map(toLotIdentityCandidate))
  return token === 'unknown-lot' ? null : token
}

const resolveReportRowLotId = (row: ReportRow): string | null => {
  const directLotId = toTrimmedString(row.lotId)
  if (directLotId) return directLotId

  const lotKey = toTrimmedString(row.lotKey)
  const rowId = toTrimmedString(row.id)
  if (lotKey && rowId) return rowId

  return null
}

const matchesReportScopeLot = (
  row: ReportRow,
  lotIds: ReadonlySet<string>,
  lotKeys: ReadonlySet<string>
): boolean => {
  const lotId = resolveReportRowLotId(row)
  const lotToken = resolveReportLotToken(row.lotKey, row.lot)

  if (lotIds.size > 0) {
    if (lotId && lotIds.has(lotId)) {
      return true
    }

    if (lotKeys.size === 0 || !lotToken || !lotKeys.has(lotToken)) {
      return false
    }

    if (!lotId) {
      return true
    }

    const lotIdToken = resolveReportLotToken(lotId)
    return lotIdToken === lotToken
  }

  if (lotKeys.size > 0 && lotToken) {
    return lotKeys.has(lotToken)
  }

  return lotKeys.size === 0
}

export const getRowTime = (row: ReportRow): Date | null => {
  const candidates = [
    row.recordedAt,
    row.date,
    row.dueAt,
    row.recordDate,
    row.stockingDate,
    row.harvestDate,
    row.updatedAt,
    row.createdAt
  ]

  for (const candidate of candidates) {
    const iso = toTrimmedString(candidate)
    if (!iso) continue
    const parsed = new Date(iso)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }

  return null
}

const buildFromTime = (ymd: string): number | null => {
  const trimmed = ymd.trim()
  if (!trimmed) return null
  const t = new Date(`${trimmed}T00:00:00.000Z`).getTime()
  return Number.isNaN(t) ? null : t
}

const buildToTime = (ymd: string): number | null => {
  const trimmed = ymd.trim()
  if (!trimmed) return null
  const t = new Date(`${trimmed}T23:59:59.999Z`).getTime()
  return Number.isNaN(t) ? null : t
}

/** Findings #223: newest rows first; rows without a date keep their order, at the end. */
export const sortRowsNewestFirst = <T extends ReportRow>(rows: readonly T[]): T[] => {
  const timeOf = (row: T): number => getRowTime(row)?.getTime() ?? Number.NEGATIVE_INFINITY
  return rows.slice().sort((a, b) => {
    const [aTime, bTime] = [timeOf(a), timeOf(b)]
    return aTime === bTime ? 0 : bTime > aTime ? 1 : -1
  })
}

export const applyReportScope = (rows: ReportRow[], scope: ReportScopeV1 | null | undefined): ReportRow[] => {
  if (!scope) return rows

  const fromTime = buildFromTime(scope.fromDate)
  const toTime = buildToTime(scope.toDate)
  const hasDateRange = fromTime !== null || toTime !== null

  const farmIds = new Set(scope.farmIds)
  const productionUnitIds = new Set(scope.productionUnitIds)
  const lotIds = new Set(scope.lotIds)
  const lotKeys = new Set(
    scope.lotKeys
      .map((lotKey) => resolveReportLotToken(lotKey))
      .filter((lotKey): lotKey is string => Boolean(lotKey))
  )

  return rows.filter((row) => {
    const farmId = toTrimmedString(row.farmId)
    if (farmIds.size > 0 && farmId && !farmIds.has(farmId)) return false

    const productionUnitId = toTrimmedString(row.productionUnitId)
    if (productionUnitIds.size > 0 && productionUnitId && !productionUnitIds.has(productionUnitId)) return false

    if (!matchesReportScopeLot(row, lotIds, lotKeys)) {
      return false
    }

    if (!hasDateRange) return true
    const time = getRowTime(row)
    if (!time) return true
    const t = time.getTime()
    if (fromTime !== null && t < fromTime) return false
    if (toTime !== null && t > toTime) return false
    return true
  })
}
